"use client";

import { useEffect, useRef, useState } from "react";
import { io, Socket } from "socket.io-client";

export const useSocket = () => {
	const socketRef = useRef<Socket | null>(null);
	const [isConnected, setIsConnected] = useState(false);
	const connectionTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	useEffect(() => {
		// Initialize socket connection
		const socketInitializer = async () => {
			try {
				console.log("Attempting to connect to Socket.io...");
				socketRef.current = io("wss://localhost:3001", {
					query: { statusCheck: "true" },
					transports: ["websocket", "polling"],
					timeout: 10000, // Increase connection timeout to 10 seconds
					reconnectionAttempts: 3, // Limit reconnection attempts
					reconnectionDelay: 1000, // Start with 1 second delay
				});

				// Clear timeout when connection succeeds
				const clearConnectionTimeout = () => {
					if (connectionTimeoutRef.current) {
						clearTimeout(connectionTimeoutRef.current);
						connectionTimeoutRef.current = null;
					}
				};

				socketRef.current.on("connect", () => {
					console.log("Connected to Socket.io server");
					clearConnectionTimeout();
					setIsConnected(true);
				});

				socketRef.current.on("disconnect", (reason) => {
					console.log("Disconnected from Socket.io server", reason);
					setIsConnected(false);
				});

				socketRef.current.on("auth:success", (data) => {
					console.log("Authentication successful for status check", data);
					clearConnectionTimeout();
					setIsConnected(true);
				});

				socketRef.current.on("auth:failed", (reason) => {
					console.log("Authentication failed:", reason);
					clearConnectionTimeout();
					setIsConnected(false);
				});

				socketRef.current.on("connect_error", (error) => {
					console.error("Socket.io connection error:", error);
					clearConnectionTimeout();
					setIsConnected(false);
				});

				// Set a timeout to detect connection failures
				connectionTimeoutRef.current = setTimeout(() => {
					if (!socketRef.current?.connected) {
						console.error("Socket.io connection timeout - server may not be running");
						socketRef.current?.disconnect();
						setIsConnected(false);
					}
				}, 8000); // Increased to 8 seconds
			} catch (error) {
				console.error("Failed to initialize Socket.io:", error);
				setIsConnected(false);
			}
		};

		socketInitializer();

		return () => {
			if (connectionTimeoutRef.current) {
				clearTimeout(connectionTimeoutRef.current);
			}
			if (socketRef.current) {
				socketRef.current.disconnect();
			}
		};
	}, []);

	return { socket: socketRef.current, isConnected };
};
